import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { classifyDelivery, type DeliveryQuote } from '@/lib/delivery'
import { cn } from '@/lib/cn'
import { currentMonthInMalaysia, formatDate, formatMonthYear, todayInMalaysia } from '@/lib/date'
import { shiftMonth, useCapacityCalendar } from '@/components/order/CapacityCalendar'

function paddingFor(firstDate: string): unknown[] {
  const day = new Date(firstDate + 'T00:00:00').getDay()
  return Array.from({ length: (day + 6) % 7 })
}

/** "1200 left" overflowed narrow mobile cells; cap the figure. */
function compact(n: number): string {
  return n > 999 ? '999+' : String(n)
}

/**
 * The delivery date calendar, for checkout and the manual order form (client
 * request 2026-10-06). It looks and greys out exactly like the production
 * calendar it replaced - the day's quota ("N left" / "Full"), past, closed and
 * full days off - and adds one thing: the picked day decides urgent or
 * normal. Production starts on the first day with room; a day before the
 * standard estimate carries a red Urgent pill and is charged the rush rate,
 * unless the order cannot be rushed, when it is a normal order made for that
 * date. Nobody is bound by the SLAs: any day with quota is open, to customers
 * and the team alike, and the server fits the sections into the days picked.
 *
 * `variant` picks the surface recipe: the storefront's monochrome cells at
 * checkout, the portal's at the manual order form. `urgentNote` is what the
 * caller prints beside an urgent pick, e.g. "+RM 12.00".
 */
export default function DeliveryDatePicker({
  quote,
  loading,
  value,
  onChange,
  pieces,
  countsQuota,
  variant,
  urgentNote,
  rushFor,
}: {
  quote: DeliveryQuote | undefined
  loading?: boolean
  value: string | null
  onChange: (date: string | null) => void
  pieces: number
  /** Grey out days without room for these pieces; off for a quota-exempt manual order. */
  countsQuota: boolean
  variant: 'storefront' | 'portal'
  urgentNote?: string | null
  /** What picking a date adds as urgent, e.g. "+RM 80.00" - shown when hovering an urgent day. */
  rushFor?: (date: string) => string | null
}) {
  const [month, setMonth] = useState(currentMonthInMalaysia)
  const today = todayInMalaysia()
  const calendar = useCapacityCalendar(month)
  const storefront = variant === 'storefront'

  // Open on the month production starts in, once, so a late-month order does
  // not land on a month of greyed-out days.
  const startMonth = quote?.production_date.slice(0, 7)
  useEffect(() => {
    if (startMonth && startMonth > month) setMonth(startMonth)
    // Only when the start month first arrives or moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startMonth])

  // More pieces or another product can move the dates; a pick the new quote
  // no longer allows is cleared rather than left to fail at placement.
  useEffect(() => {
    if (value && quote && !classifyDelivery(value, quote).allowed) onChange(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, value])

  const needed = Math.max(1, pieces)
  // Whether any day can carry the Urgent pill: rushability is per date, since
  // a product may rush in four days but not in two.
  const anyUrgent = Boolean(quote && Object.values(quote.urgent_by_date ?? {}).some((day) => day.rushable))
  const verdict = value && quote ? classifyDelivery(value, quote) : null
  const shortButNotRushed = Boolean(value && quote && value < quote.standard_date && !verdict?.urgent)
  // Shorter than the section walk for this service level: the server fits
  // the sections into the days picked (SectionScheduleService::fitToDate).
  const fitted = Boolean(value && quote && value < (verdict?.urgent ? quote.urgent_date : quote.standard_date))
  const arrowClass = storefront
    ? 'flex size-9 items-center justify-center rounded-full border border-foreground/15 text-foreground/70 transition-colors hover:bg-foreground/5'
    : 'flex size-9 items-center justify-center rounded-lg text-foreground/70 hover:bg-foreground/5'

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-semibold">{formatMonthYear(month)}</span>
        <div className="flex items-center gap-2">
          <button type="button" aria-label="Previous month" className={arrowClass} onClick={() => setMonth(shiftMonth(month, -1))}>
            <ChevronLeft className="size-4" />
          </button>
          <button type="button" aria-label="Next month" className={arrowClass} onClick={() => setMonth(shiftMonth(month, 1))}>
            <ChevronRight className="size-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} className="pb-1 text-center text-2xs font-bold uppercase text-foreground/50">
            {d}
          </div>
        ))}
        {calendar.data && calendar.data.days.length > 0 && paddingFor(calendar.data.days[0].date).map((_, i) => <div key={`pad-${i}`} />)}
        {calendar.data?.days.map((day) => {
          const closed = !day.working || day.blocked
          const full = day.remaining < needed
          const rule = quote ? classifyDelivery(day.date, quote) : null
          const disabled = day.past || closed || (countsQuota && full) || !rule?.allowed
          const urgentDay = !disabled && Boolean(rule?.urgent)
          const selected = value === day.date
          const isToday = !selected && day.date === today
          const low = !disabled && day.remaining < needed * 2
          return (
            <button
              type="button"
              key={day.date}
              disabled={disabled}
              onClick={() => onChange(day.date)}
              aria-current={day.date === today ? 'date' : undefined}
              aria-pressed={selected}
              title={urgentDay ? `Urgent${rushFor?.(day.date) ? ` ${rushFor(day.date)}` : ''} - shorter than our normal production time` : undefined}
              className={cn(
                'relative flex aspect-square flex-col items-center justify-center gap-0.5 rounded-lg border p-0.5 text-center sm:aspect-auto sm:min-h-[4.5rem] sm:p-1',
                selected && (storefront ? 'border-foreground bg-foreground text-white' : 'border-primary bg-primary text-white'),
                !selected &&
                  !disabled &&
                  (urgentDay
                    ? 'border-danger/50 bg-danger/5 hover:border-danger'
                    : storefront
                      ? 'border-foreground/10 bg-background hover:border-foreground/40'
                      : 'border-line bg-surface hover:border-primary/50'),
                !selected &&
                  disabled &&
                  (storefront
                    ? 'cursor-not-allowed border-foreground/5 bg-foreground/[0.03] text-foreground/30'
                    : 'cursor-not-allowed border-line/60 bg-app text-muted/50'),
                !selected && day.blocked && 'text-danger/50',
                isToday && (storefront ? 'ring-1 ring-foreground/40' : 'ring-2 ring-primary'),
              )}
            >
              <span className={cn('text-sm font-bold', urgentDay && !selected && 'text-danger')}>{Number(day.date.slice(8))}</span>
              {day.blocked ? (
                <span className="hidden text-2xs font-semibold sm:block">Closed</span>
              ) : !day.working ? (
                <span className="hidden text-2xs sm:block">-</span>
              ) : (
                <>
                  <span className={cn('hidden text-2xs font-semibold sm:block', !selected && (full ? 'text-danger' : 'text-foreground/50'))}>
                    {full && !day.past ? 'Full' : `${compact(day.remaining)} left`}
                  </span>
                  <span
                    className={cn(
                      'text-2xs font-semibold leading-none sm:hidden',
                      !selected && (full ? 'text-danger' : low ? 'text-warning' : 'text-foreground/50'),
                    )}
                  >
                    {full && !day.past ? 'Full' : compact(day.remaining)}
                  </span>
                </>
              )}
              {/* The pill from sm up, a dot on phones where a word does not
                  fit; both stay on the selected day, inverted. */}
              {urgentDay && (
                <>
                  <span
                    className={cn(
                      'hidden rounded-full px-1.5 py-px text-[10px] font-bold uppercase leading-tight sm:inline-block',
                      selected ? 'bg-white text-danger' : 'bg-danger text-white',
                    )}
                  >
                    Urgent
                  </span>
                  <span
                    aria-hidden="true"
                    className={cn('absolute right-1 top-1 size-1.5 rounded-full sm:hidden', selected ? 'bg-white' : 'bg-danger')}
                  />
                </>
              )}
            </button>
          )
        })}
      </div>

      <p className="mt-2 text-2xs text-foreground/50">
        Numbers show the pieces still available that day; greyed days are full or closed.
        {/* Only when a day can actually carry the pill. */}
        {anyUrgent && (
          <>
            {' '}Dates marked{' '}
            <span className="rounded-full bg-danger px-1.5 py-px text-[10px] font-bold uppercase text-white">Urgent</span> are shorter than
            our normal production time, so the urgent rate applies.
          </>
        )}
      </p>

      <div className={cn('mt-4 rounded-lg border p-3 text-sm', storefront ? 'border-foreground/10 bg-foreground/[0.03]' : 'border-line bg-app')}>
        {loading || !quote ? (
          <p className="text-foreground/60">Working out the dates...</p>
        ) : (
          <>
            <p>
              Production starts <span className="font-bold">{formatDate(quote.production_date)}</span>
            </p>
            {value ? (
              <>
                <p className="mt-1">
                  Delivery date <span className="font-bold">{formatDate(value)}</span>
                  {verdict?.urgent ? (
                    <span className="ml-1.5 inline-block rounded-full bg-danger px-2 py-0.5 text-xs font-bold text-white">
                      Urgent service{urgentNote ? ` ${urgentNote}` : ''}
                    </span>
                  ) : (
                    <span className="text-foreground/60"> - standard</span>
                  )}
                </p>
                {fitted && (
                  <p className="mt-1 text-2xs font-semibold text-warning">
                    Shorter than our normal production time - we schedule every section to make your date.
                  </p>
                )}
                {shortButNotRushed && (
                  <p className="mt-1 text-2xs font-semibold text-warning">
                    {quote.urgent_by_date?.[value]?.blocker ?? quote.rush_blocker ?? 'This order cannot be rushed that fast'}, so it is made as a
                    normal order for your date.
                  </p>
                )}
              </>
            ) : (
              <p className="mt-1 text-foreground/70">
                Normal production is ready by <span className="font-bold">{formatDate(quote.standard_date)}</span>.
                {anyUrgent ? ' Pick an earlier date for urgent service.' : ''}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
